// App entry: routes between overview, river drawer, and blog.
//
// Routing model:
//   #/             → overview (drawer closed, map fit to all rivers)
//   #/r/:id        → overview + drawer overlay focused on that river
//   #/blog         → devotional listing
//   #/blog/:slug   → devotional entry
//
// The overview (map + cards) is mounted ONCE and never torn down — the map
// keeps its state across drawer open/close and lens-filter toggles, so users
// don't lose their place.

import { fetchRivers, fetchSummary, fetchDevotionalIndex, fetchDevotionalEntry } from './lib/api';
import { renderStaleBanner, type BannerState } from './components/stale-banner';
import { setRouteMeta, type RouteMeta } from './lib/meta';
import { renderHero } from './components/hero';
import { renderOverview, resetFilters } from './components/overview';
import { openDrawer, closeDrawer, currentDrawerId, isDrawerOpen } from './components/river-drawer';
import { getMode, onLensChange } from './lib/mode';
import { onRegionChange } from './lib/regions';
import { onLocationChange, getStoredLocation } from './lib/geolocation';
import { renderPrivacy, renderCookies } from './components/legal';
import { maybeShowConsentBanners } from './components/consent-banners';
import { currentRoute, onRouteChange, navigate, type Route } from './lib/router';
import { initTheme, getThemePref, setThemePref, onThemeChange, type ThemePref } from './lib/theme';
import type { RegionalSummary, RiverAnalysis } from '../functions/_lib/types';

// How often to poll for a fresher snapshot. The snapshot itself rebuilds
// every 15 min via the cron worker, so checking more often than that wastes
// requests. We poll a bit faster (10 min) so users don't sit on a stale snap
// for the full 15 min if they happen to load right before a rebuild.
const REFRESH_MS = 10 * 60 * 1000;

// Minimum time between refreshes triggered by ANY signal (interval,
// visibility-change, user click). Prevents the chatty
// switch-tab-and-back-burns-AI-calls pattern.
const REFRESH_THROTTLE_MS = 5 * 60 * 1000;
let lastRefreshAt = 0;

let lastRivers: RiverAnalysis[] = [];
// Three summary slots: the per-lens ones (legacy fallback) + the new
// combined summary that's used by the hero in the unified-lens world.
let lastSummaries: {
  whitewater: RegionalSummary | null;
  fly_fishing: RegionalSummary | null;
  combined: RegionalSummary | null;
} = { whitewater: null, fly_fishing: null, combined: null };
let lastGeneratedAt: string | null = null;
let mapLoaded = false;
let mapModule: typeof import('./components/river-map') | null = null;
let overviewMounted = false;
let bannerState: BannerState = { kind: 'hidden' };

function paintBanner(): void {
  const host = document.getElementById('stale-banner-host');
  if (!host) return;
  host.innerHTML = renderStaleBanner(bannerState);
  const retry = host.querySelector<HTMLButtonElement>('[data-stale-retry]');
  if (retry) retry.addEventListener('click', () => void refresh({ force: true }));
}

function shell(): string {
  // Two layouts in one shell:
  //   Desktop (lg+): app-shell with sticky header, fixed-height split.
  //     LEFT column is the map (locked — never scrolls with the page).
  //     RIGHT column scrolls independently (hero, filters, cards, footer).
  //   Mobile/tablet: normal page flow, everything scrolls together.
  return `
    <div class="app-shell">
      <a href="#route-host" class="skip-link">Skip to main content</a>
      <header class="app-header">
        <div class="container mx-auto px-4 max-w-7xl flex items-center justify-between flex-wrap gap-4">
          <a href="#/" class="flex items-center gap-4 group">
            <img src="/frozen-fly-mascot.png" alt="Frozen Fly" class="brand-mascot group-hover:scale-105 transition-transform" />
            <div class="leading-tight">
              <div class="font-display text-xl md:text-2xl text-primary tracking-wider">THE FROZEN FLY</div>
              <div class="tagline-ribbon is-block mt-0.5">Tie It · Cast It · Float It</div>
            </div>
          </a>
          <div class="flex items-center gap-3">
            <nav class="flex items-center gap-0.5">
              <a href="#/" id="nav-flows" class="nav-link is-active">Flows</a>
              <a href="#/blog" id="nav-breathe" class="nav-link">Breathe</a>
            </nav>
            <div id="theme-toggle-host" class="theme-toggle" role="group" aria-label="Color theme"></div>
          </div>
        </div>
      </header>

      <main id="route-host" class="app-main" tabindex="-1"></main>
    </div>
  `;
}

function overviewLayout(): string {
  // The map column is LOCKED to the viewport — page wheel events over the
  // map zoom the map without scrolling the surrounding page. The right
  // column (hero + filters + cards + footer) scrolls independently inside
  // its own container.
  return `
    <div class="overview-split">
      <div id="map-host" class="map-host overview-map">
        <div class="w-full h-full grid place-items-center text-base-content/30 text-sm bg-base-200/40">
          <div class="text-center">
            <div class="loading loading-ring loading-lg text-primary mb-2"></div>
            <div class="font-display text-xs tracking-widest uppercase opacity-60">Mapping the West</div>
          </div>
        </div>
      </div>
      <div class="overview-scroll">
        <div class="overview-inner">
          <div id="stale-banner-host"></div>
          <div id="hero-host"></div>
          <div id="overview-host">${renderCardSkeleton()}</div>
          <footer class="overview-footer">
            <div class="tagline-ribbon">Western Montana · Est. 1977</div>
            <div class="text-xs text-base-content/40 mt-2">
              Data: <a class="link link-hover" href="https://waterservices.usgs.gov" target="_blank" rel="noreferrer">USGS</a>
              · <a class="link link-hover" href="https://water.noaa.gov" target="_blank" rel="noreferrer">NOAA NWPS</a>
              · <a class="link link-hover" href="https://api.water.usgs.gov/nldi" target="_blank" rel="noreferrer">NLDI</a>
              · Reports via Anthropic + Tavily
            </div>
            <div class="text-xs text-base-content/35 mt-2">
              <a class="link link-hover" href="#/privacy">Privacy</a>
              · <a class="link link-hover" href="#/cookies">Cookies & Storage</a>
              · <a class="link link-hover" href="https://github.com/mjerrow/the-frozen-fly" target="_blank" rel="noreferrer">Source</a>
            </div>
          </footer>
        </div>
      </div>
    </div>
  `;
}

function renderCardSkeleton(): string {
  const card = `
    <div class="rounded-2xl bg-base-200/40 border border-base-content/5 p-4 pl-5 relative overflow-hidden">
      <div class="absolute top-0 left-0 bottom-0 w-1 bg-base-300/60"></div>
      <div class="skeleton h-5 w-3/4 mb-2 bg-base-300/40"></div>
      <div class="skeleton h-3 w-1/2 mb-4 bg-base-300/40"></div>
      <div class="flex items-end gap-4">
        <div class="skeleton h-8 w-20 bg-base-300/40"></div>
        <div class="skeleton h-9 flex-1 ml-auto bg-base-300/40"></div>
      </div>
    </div>
  `;
  return `<div class="grid grid-cols-1 md:grid-cols-2 gap-4">${card.repeat(4)}</div>`;
}

// ──────────────────────────────────────────────────────────────────────
// Overview lifecycle — mounted once, kept alive across all routes.
// ──────────────────────────────────────────────────────────────────────

async function ensureOverviewMounted(): Promise<void> {
  if (overviewMounted) return;
  const host = document.getElementById('route-host');
  if (!host) return;
  host.innerHTML = `<div id="overview-container">${overviewLayout()}</div>`;
  renderOverviewState();
  overviewMounted = true;

  // Lazy-mount the map AFTER the cards paint.
  const mountMap = () => { void ensureMapMounted().then(() => {
    if (mapModule) mapModule.updateMap(lastRivers, getMode());
  }); };
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(mountMap, { timeout: 800 });
  } else {
    setTimeout(mountMap, 100);
  }
}

function renderOverviewState(): void {
  const heroHost = document.getElementById('hero-host');
  const overviewHost = document.getElementById('overview-host');
  // Hero: prefer the combined cross-lens summary; fall back to whichever
  // per-lens summary we have if combined hasn't been generated yet.
  const heroSummary =
    lastSummaries.combined
    ?? lastSummaries.whitewater
    ?? lastSummaries.fly_fishing;
  if (heroHost) heroHost.innerHTML = renderHero(heroSummary, lastGeneratedAt);
  if (overviewHost) overviewHost.innerHTML = renderOverview(lastRivers, onCardHover);
}

async function ensureMapMounted(): Promise<void> {
  if (mapLoaded) return;
  const el = document.getElementById('map-host');
  if (!el) return;
  mapModule = await import('./components/river-map');
  el.innerHTML = '';
  await mapModule.mountMap(el, { onHover: onMapHover, onSelect: openRiver });
  mapLoaded = true;
  // If the user already has a stored location, render the pin immediately.
  const loc = getStoredLocation();
  if (loc) mapModule.showUserLocation(loc);
}

// ──────────────────────────────────────────────────────────────────────
// Route handler — toggles between flows (overview/river) and blog views.
// ──────────────────────────────────────────────────────────────────────

function updateNavState(activeNav: 'flows' | 'blog' | 'legal'): void {
  const flows = document.getElementById('nav-flows');
  const breathe = document.getElementById('nav-breathe');
  if (flows) flows.classList.toggle('is-active', activeNav === 'flows');
  if (breathe) breathe.classList.toggle('is-active', activeNav === 'blog');
}

function ensureLegalContainer(): HTMLElement {
  let el = document.getElementById('legal-container');
  if (!el) {
    const host = document.getElementById('route-host');
    if (host) {
      el = document.createElement('div');
      el.id = 'legal-container';
      el.className = 'legal-container';
      host.appendChild(el);
    }
  }
  return el!;
}

function ensureBlogContainer(): HTMLElement {
  let el = document.getElementById('blog-container');
  if (!el) {
    const host = document.getElementById('route-host');
    if (host) {
      el = document.createElement('div');
      el.id = 'blog-container';
      el.className = 'blog-container';
      host.appendChild(el);
    }
  }
  return el!;
}

function metaForRoute(route: Route): RouteMeta {
  switch (route.name) {
    case 'overview':
      return {
        title: 'The Frozen Fly — Tie It. Cast It. Float It.',
        description: 'Real-time flows, runnable status, and AI-summarized fishing reports for the major rivers of the western US.',
      };
    case 'river': {
      const r = lastRivers.find((x) => x.id === route.id);
      if (!r) return {
        title: 'River — Frozen Fly',
        description: 'Flow + fishing + whitewater report.',
      };
      const cfs = r.current?.cfs;
      const cfsPart = cfs != null ? `${cfs} cfs · ` : '';
      return {
        title: `${r.river} — ${r.section} | Frozen Fly`,
        description: `${cfsPart}${r.percentile?.label ?? ''}. AI-summarized fishing + whitewater report.`,
      };
    }
    case 'blog':
      return {
        title: 'Breathe — devotionals | Frozen Fly',
        description: "Outdoor devotionals from the Breathing in God's World series.",
      };
    case 'blog-entry':
      // Placeholder — upgraded after fetchDevotionalEntry resolves.
      return {
        title: 'Reading — Frozen Fly',
        description: 'A devotional from the Breathe series.',
      };
    case 'privacy':
      return {
        title: 'Privacy | Frozen Fly',
        description: 'How Frozen Fly handles your data.',
      };
    case 'cookies':
      return {
        title: 'Cookies | Frozen Fly',
        description: 'How Frozen Fly uses local storage and cookies.',
      };
  }
}

async function renderRoute(route: Route): Promise<void> {
  // Set placeholder meta synchronously so the browser tab + crawler
  // can-load see route-appropriate values immediately. Data-dependent
  // routes (river / blog-entry) upgrade meta after fetch resolves
  // below.
  setRouteMeta(metaForRoute(route));

  const isBlog = route.name === 'blog' || route.name === 'blog-entry';
  const isLegal = route.name === 'privacy' || route.name === 'cookies';
  updateNavState(isBlog ? 'blog' : isLegal ? 'legal' : 'flows');

  // Hide non-active panels
  const ovEl = document.getElementById('overview-container');
  const blogElExisting = document.getElementById('blog-container');
  const legalElExisting = document.getElementById('legal-container');

  if (isLegal) {
    if (isDrawerOpen()) closeDrawer();
    if (ovEl) ovEl.style.display = 'none';
    if (blogElExisting) blogElExisting.style.display = 'none';
    const legalEl = ensureLegalContainer();
    legalEl.style.display = '';
    legalEl.innerHTML = route.name === 'privacy' ? renderPrivacy() : renderCookies();
    window.scrollTo(0, 0);
    return;
  }

  if (isBlog) {
    if (isDrawerOpen()) closeDrawer();
    if (ovEl) ovEl.style.display = 'none';
    if (legalElExisting) legalElExisting.style.display = 'none';

    const blogEl = ensureBlogContainer();
    blogEl.style.display = '';

    if (route.name === 'blog') {
      const { renderBlogListing, renderBlogListingError } = await import('./components/blog-listing');
      const result = await fetchDevotionalIndex();
      blogEl.innerHTML = result.error
        ? renderBlogListingError(result.error)
        : renderBlogListing(result.data);
      window.scrollTo(0, 0);
      const { teardownReadingProgress } = await import('./components/blog-entry');
      teardownReadingProgress();
    } else {
      const { renderBlogEntry, renderBlogNotFound, setupReadingProgress } =
        await import('./components/blog-entry');
      const result = await fetchDevotionalEntry(route.slug);
      if (result.error) {
        blogEl.innerHTML = renderBlogNotFound(result.error);
      } else if (!result.data) {
        blogEl.innerHTML = renderBlogNotFound();
      } else {
        const entry = result.data;
        blogEl.innerHTML = renderBlogEntry(entry);
        setupReadingProgress();
        // Upgrade meta now that we have the entry's frontmatter.
        setRouteMeta({
          title: `${entry.title} | Breathe`,
          description: entry.description ?? entry.excerpt ?? 'Devotional from the Breathe series.',
          image: `/data/devotionals/imagery/entry-${entry.slug}.webp`,
        });
      }
      window.scrollTo(0, 0);
    }
    return;
  }

  // ── Flows / river routes ────────────────────────────────────────────
  const blogEl = document.getElementById('blog-container');
  if (blogEl) blogEl.style.display = 'none';
  if (legalElExisting) legalElExisting.style.display = 'none';
  import('./components/blog-entry').then((m) => m.teardownReadingProgress()).catch(() => {});

  if (ovEl) ovEl.style.display = '';

  await ensureOverviewMounted();

  if (route.name === 'overview') {
    if (isDrawerOpen()) closeDrawer();
    if (mapModule) mapModule.fitAllRivers();
    return;
  }

  if (route.name === 'river') {
    if (currentDrawerId() === route.id) return;
    const fallback = lastRivers.find((r) => r.id === route.id);
    void openDrawer(route.id, fallback, {
      onClose: () => {
        if (window.location.hash !== '#/' && window.location.hash !== '') {
          navigate({ name: 'overview' });
        }
        if (mapModule) mapModule.fitAllRivers();
      },
    });
    if (mapModule) {
      mapModule.focusRiver(route.id);
      mapModule.highlightRiver(route.id, { flyTo: false });
    }
  }
}

// Snapshot age above which the stale banner appears. Cron is 15 min;
// 25 min is roughly one missed cron cycle's worth of delay.
const STALE_THRESHOLD_MIN = 25;

async function refresh(opts: { force?: boolean } = {}): Promise<void> {
  const now = Date.now();
  if (!opts.force && now - lastRefreshAt < REFRESH_THROTTLE_MS) return;
  lastRefreshAt = now;

  // Offline short-circuit — don't even try the fetch.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    bannerState = { kind: 'offline' };
    paintBanner();
    return;
  }

  try {
    // Fetch rivers (returns { data, meta, error } — never throws) +
    // summaries (still throw on failure, caught at call site).
    const [riversResult, combined, ww, ff] = await Promise.all([
      fetchRivers(),
      fetchSummary('combined' as any).catch(() => null),
      fetchSummary('whitewater').catch(() => null),
      fetchSummary('fly_fishing').catch(() => null),
    ]);

    if (riversResult.error) {
      bannerState = {
        kind: 'error',
        message: riversResult.error.message,
        retry_after_s: riversResult.error.retry_after_s,
      };
      paintBanner();
      return;
    }

    lastRivers = riversResult.data!.rivers;
    lastGeneratedAt = riversResult.meta!.generated_at;
    lastSummaries = { combined, whitewater: ww, fly_fishing: ff };

    bannerState =
      riversResult.meta!.age_minutes > STALE_THRESHOLD_MIN
        ? { kind: 'stale', age_minutes: riversResult.meta!.age_minutes }
        : { kind: 'hidden' };
    paintBanner();

    if (overviewMounted) {
      renderOverviewState();
      if (mapModule) mapModule.updateMap(lastRivers, getMode());
    }

    const id = currentDrawerId();
    if (id) {
      const r = lastRivers.find((x) => x.id === id);
      if (r) void openDrawer(id, r, {
        onClose: () => {
          if (window.location.hash !== '#/' && window.location.hash !== '') {
            navigate({ name: 'overview' });
          }
          if (mapModule) mapModule.fitAllRivers();
        },
      });
    }
  } catch (e) {
    // Unexpected: summary errors are caught at call sites; fetchRivers
    // never throws (returns error in result). Anything reaching here is
    // a programming error — surface via the banner.
    bannerState = { kind: 'error', message: (e as Error).message };
    paintBanner();
  }
}

function openRiver(id: string): void {
  window.location.hash = `#/r/${id}`;
}

function onCardHover(id: string | null): void {
  // Hover highlights line on the map — never flies to. Geography stays put.
  if (mapModule) mapModule.highlightRiver(id, { flyTo: false });
}

function mountThemeToggle(): void {
  const host = document.getElementById('theme-toggle-host');
  if (!host) return;
  const pref = getThemePref();
  // Tri-state segmented control. Icons match macOS / iOS conventions.
  host.innerHTML = `
    <button class="theme-pill ${pref === 'light' ? 'is-active' : ''}" data-theme-pick="light" aria-label="Light theme" aria-pressed="${pref === 'light'}">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
    </button>
    <button class="theme-pill ${pref === 'system' ? 'is-active' : ''}" data-theme-pick="system" aria-label="System theme" aria-pressed="${pref === 'system'}">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="14" rx="2"/><path d="M8 22h8M12 18v4"/></svg>
    </button>
    <button class="theme-pill ${pref === 'dark' ? 'is-active' : ''}" data-theme-pick="dark" aria-label="Dark theme" aria-pressed="${pref === 'dark'}">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
    </button>
  `;
  host.querySelectorAll<HTMLButtonElement>('[data-theme-pick]').forEach((btn) => {
    btn.addEventListener('click', () => {
      setThemePref(btn.dataset.themePick as ThemePref);
    });
  });
}

function onMapHover(id: string | null): void {
  document.querySelectorAll<HTMLElement>('.river-card.is-map-hover').forEach((el) => el.classList.remove('is-map-hover'));
  if (id) {
    const card = document.querySelector<HTMLElement>(`.river-card[data-id="${id}"]`);
    if (card) {
      card.classList.add('is-map-hover');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
}

async function boot(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = shell();

  // Theme: subscribe to system preference changes, then mount the toggle
  // and re-render anything that bakes color into JS-mounted DOM (map
  // basemap, sparkline/hydrograph palettes, drawer chrome).
  initTheme();
  mountThemeToggle();
  onThemeChange(() => {
    mountThemeToggle();  // re-render so the active pill matches.
    if (mapModule) mapModule.applyTheme();
    // Charts cache palette on init. Re-render the current route so any
    // mounted hydrograph/sparkline gets a fresh paint with the new colors.
    if (overviewMounted) renderOverviewState();
    if (isDrawerOpen()) {
      const id = currentDrawerId();
      if (id) void renderRoute({ name: 'river', id });
    }
  });

  // Hidden admin refresh — Cmd/Ctrl + Shift + R bypasses browser reload
  // (the browser still catches plain Cmd+R) and forces our snapshot refetch.
  window.addEventListener('keydown', (e) => {
    if (e.key === 'R' && e.shiftKey && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void refresh({ force: true });
    }
  });

  // Online / offline — flip banner state + auto-refresh when reconnecting.
  window.addEventListener('online', () => {
    bannerState = { kind: 'hidden' };
    paintBanner();
    void refresh({ force: true });
  });
  window.addEventListener('offline', () => {
    bannerState = { kind: 'offline' };
    paintBanner();
  });

  // When the user toggles which lenses are shown, re-render. Don't reset
  // filters — the lens filter IS a filter, so we want to preserve other
  // filter state (search, state, etc.) and just rebucket.
  onLensChange(() => {
    renderOverviewState();
    if (mapModule) mapModule.updateMap(lastRivers, getMode());
  });
  onRegionChange(() => {
    renderOverviewState();
  });
  onLocationChange((loc) => {
    renderOverviewState();
    if (mapModule) mapModule.showUserLocation(loc);
  });

  onRouteChange((route) => { void renderRoute(route); });

  // Pathname hydration (P010): if the user landed via the prerendered
  // static HTML at /blog/<slug>, convert the path to the canonical
  // hash route so the SPA router's existing logic takes over. Runs
  // once on boot; hash routes work unchanged.
  const blogPathMatch = location.pathname.match(/^\/blog\/([^/]+)\/?$/);
  if (blogPathMatch && !location.hash) {
    history.replaceState(null, '', `/#/blog/${blogPathMatch[1]}`);
  }

  await renderRoute(currentRoute());
  // Initial fetch — force so throttle window starts from zero.
  void refresh({ force: true });

  // First-visit consent + location prompt sequence. Idempotent — if both
  // have already been acknowledged or dismissed, this is a no-op. Fires
  // after a short delay so it doesn't fight initial paint.
  setTimeout(() => {
    maybeShowConsentBanners(() => {
      // Re-render overview so the "Near You" section / loc button reflect
      // the post-consent state (location may now be available).
      if (overviewMounted) renderOverviewState();
    });
  }, 600);

  setInterval(() => {
    if (document.visibilityState === 'visible') void refresh();
  }, REFRESH_MS);
}

void boot();
